<script>
  import { onMount, tick } from 'svelte';
  import BackToTop from './lib/components/BackToTop.svelte';
  import CookieBanner from './lib/components/CookieBanner.svelte';
  import { canonicalForPath, metaForPath } from './lib/data/seo.js';
  import { rememberLandingScroll } from './lib/utils/router.js';
  import AgreementPage from './lib/pages/AgreementPage.svelte';
  import ConductPage from './lib/pages/ConductPage.svelte';
  import LandingPage from './lib/pages/LandingPage.svelte';
  import OfferPage from './lib/pages/OfferPage.svelte';
  import PrivacyPage from './lib/pages/PrivacyPage.svelte';
  import RegistrationPage from './lib/pages/RegistrationPage.svelte';
  import TariffPage from './lib/pages/TariffPage.svelte';
  import TopicPage from './lib/pages/TopicPage.svelte';
  import AccountPage from './lib/pages/AccountPage.svelte';
  import { resolveProductSlug } from './lib/sections/registration.js';

  const routes = {
    '/tariff': 'tariff',
    '/registration': 'registration',
    '/account': 'account',
    '/offer': 'offer',
    '/privacy': 'privacy',
    '/conduct': 'conduct',
    '/agreement': 'agreement'
  };

  // Редиректы переименованных тем (старый слаг → новый)
  const TOPIC_REDIRECTS = {
    'inventory-forecast': 'ai-labor-market',
    'ai-security': 'ai-transformation',
    'world-trends': 'ai-cases',
    'ai-implementation': 'ai-transformation'
  };

  let currentPath = '/';
  let currentHash = '';
  let currentSearch = '';
  const PROMO_REF_KEY = 'forum-industry.promo-ref';

  $: topicSlug = currentPath.startsWith('/topic-') ? currentPath.slice('/topic-'.length) : null;
  $: currentPage = topicSlug ? 'topic' : routes[currentPath] ?? 'landing';

  function resolveTopicRedirect(pathname, search, hash) {
    if (!pathname.startsWith('/topic-')) return pathname;
    const slug = pathname.slice('/topic-'.length);
    const newSlug = TOPIC_REDIRECTS[slug];
    if (!newSlug) return pathname;
    const newPath = '/topic-' + newSlug;
    history.replaceState(null, '', newPath + search + hash);
    return newPath;
  }
  $: registrationProductIdOverride = parseProductIdFromSearch(currentSearch);
  // Если productId явно указан и соответствует известному тарифу (basic/business/vip
  // через ENV или fallback), берём slug по нему — UI («Ваш заказ» блок) покажет
  // тот же тариф и цену, что биллит createOrder. Это закрывает Codex-замечание:
  // раньше /registration?productId=33731 показывал «Базовый, 7 000 ₽» (default
  // slug 'basic'), а заказ создавался на VIP за 20 000.
  $: registrationProduct = resolveRegistrationProduct(currentSearch, registrationProductIdOverride);

  function resolveRegistrationProduct(search, idOverride) {
    if (idOverride) {
      const slugFromId = resolveProductSlug(idOverride);
      if (slugFromId) return slugFromId;
    }
    return parseProductFromSearch(search);
  }

  function parseProductFromSearch(search) {
    if (!search) return 'basic';
    try {
      const params = new URLSearchParams(search);
      const value = (params.get('product') ?? 'basic').toLowerCase();
      return ['online', 'basic', 'business', 'vip'].includes(value) ? value : 'basic';
    } catch {
      return 'basic';
    }
  }

  function parseProductIdFromSearch(search) {
    if (!search) return null;
    try {
      const raw = new URLSearchParams(search).get('productId');
      if (!raw) return null;
      const num = Number(raw);
      return Number.isFinite(num) && num > 0 ? num : null;
    } catch {
      return null;
    }
  }

  function normalizePath(p) {
    if (typeof p !== 'string' || p === '' || p === '/') return '/';
    return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
  }

  function capturePromoRef(search) {
    if (!search || typeof sessionStorage === 'undefined') return;
    try {
      const ref = new URLSearchParams(search).get('ref');
      if (ref) sessionStorage.setItem(PROMO_REF_KEY, ref);
    } catch {}
  }

  function getStoredPromoRef() {
    if (typeof sessionStorage === 'undefined') return '';
    try {
      return sessionStorage.getItem(PROMO_REF_KEY) || '';
    } catch {
      return '';
    }
  }

  function withPromoRef(search) {
    const ref = getStoredPromoRef();
    if (!ref) return search;
    try {
      const params = new URLSearchParams(search || '');
      if (!params.has('ref')) params.set('ref', ref);
      const next = params.toString();
      return next ? `?${next}` : '';
    } catch {
      return search;
    }
  }

  $: if (typeof document !== 'undefined') {
    const meta = metaForPath(currentPath);
    document.title = meta.title;
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.setAttribute('name', 'description');
      document.head.appendChild(descTag);
    }
    descTag.setAttribute('content', meta.description);
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute('href', canonicalForPath(currentPath));
  }

  function scrollToAnchor(hash) {
    const id = hash.slice(1);
    const target = id ? document.getElementById(id) : document.getElementById('top');
    target?.scrollIntoView();
  }

  function trackPageView() {
    if (typeof window === 'undefined' || typeof window.ym !== 'function') return;
    window.ym(109156850, 'hit', location.href, { title: document.title });
  }

  onMount(() => {
    function handleClick(e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const link = e.target.closest('a[href]');
      if (!link || link.target === '_blank') return;
      const href = link.getAttribute('href');
      if (!href) return;

      let pathname = href;
      let search = '';
      let hash = '';
      if (href.startsWith('/')) {
        const url = new URL(href, location.origin);
        pathname = normalizePath(url.pathname);
        search = url.search;
        hash = url.hash;
      }

      if (routes[pathname] || pathname.startsWith('/topic-')) {
        e.preventDefault();
        if (currentPath === '/') rememberLandingScroll();
        search = withPromoRef(search);
        pathname = resolveTopicRedirect(pathname, search, hash);
        history.pushState(null, '', pathname + search + hash);
        currentPath = pathname;
        currentSearch = search;
        currentHash = '';
        capturePromoRef(currentSearch);
        window.scrollTo(0, 0);
        tick().then(trackPageView);
        return;
      }

      if (href.startsWith('#')) {
        if (currentPath === '/') return;
        e.preventDefault();
        const search = withPromoRef('');
        history.pushState(null, '', '/' + search + href);
        currentPath = '/';
        currentSearch = search;
        currentHash = href;
        capturePromoRef(currentSearch);
        tick().then(() => {
          scrollToAnchor(href);
          trackPageView();
        });
      }
    }

    async function handlePopstate() {
      currentPath = resolveTopicRedirect(normalizePath(location.pathname), location.search, location.hash);
      currentSearch = location.search;
      currentHash = location.hash;
      capturePromoRef(currentSearch);
      await tick();
      if (currentPath === '/' && currentHash) {
        scrollToAnchor(currentHash);
      } else {
        window.scrollTo(0, 0);
      }
      trackPageView();
    }

    const rawPath = location.pathname;
    currentPath = resolveTopicRedirect(normalizePath(rawPath), location.search, location.hash);
    currentSearch = location.search;
    currentHash = location.hash;
    if (rawPath !== currentPath) {
      history.replaceState(null, '', currentPath + currentSearch + currentHash);
    }
    capturePromoRef(currentSearch);

    // Авто-редирект на /account при заходе на главную с активной сессией ЛК.
    // Только при первой загрузке — последующие popstate-навигации не перебрасываем,
    // чтобы пользователь мог вернуться на лендинг через «Назад» из кабинета.
    // Хеш-якорь (/#partners и т.п.) уважаем — это намеренная попытка увидеть лендинг.
    if (
      currentPath === '/' &&
      !currentHash &&
      typeof sessionStorage !== 'undefined'
    ) {
      let token = '';
      try {
        token = sessionStorage.getItem('forum-industry.account-token') || '';
      } catch {}
      if (token) {
        history.replaceState(null, '', '/account');
        currentPath = '/account';
        currentSearch = '';
      }
    }

    if (currentPath === '/' && currentHash) {
      tick().then(() => scrollToAnchor(currentHash));
    }

    window.addEventListener('click', handleClick, true);
    window.addEventListener('popstate', handlePopstate);

    return () => {
      window.removeEventListener('click', handleClick, true);
      window.removeEventListener('popstate', handlePopstate);
    };
  });
</script>

{#if currentPage === 'tariff'}
  <TariffPage />
{:else if currentPage === 'registration'}
  <RegistrationPage product={registrationProduct} productIdOverride={registrationProductIdOverride} />
{:else if currentPage === 'account'}
  <AccountPage />
{:else if currentPage === 'topic'}
  <TopicPage slug={topicSlug} />
{:else if currentPage === 'offer'}
  <OfferPage />
{:else if currentPage === 'privacy'}
  <PrivacyPage />
{:else if currentPage === 'conduct'}
  <ConductPage />
{:else if currentPage === 'agreement'}
  <AgreementPage />
{:else}
  <LandingPage />
{/if}

<BackToTop />
<CookieBanner />
